import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Encounter } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { useDisclosure } from "@mantine/hooks";

import {
  LABOR_ENCOUNTER_CLOSEOUT_CONFIRMATION,
  LABOR_ENCOUNTER_LOOKUP_ERROR,
} from "@/clients/constants/pregnancy-closeout";
import { getActiveLaborAndDeliveryEncounter } from "@/clients/services/encounter";
import { toast } from "@/utils/notifications";

interface UseLaborEncounterCloseoutGuardOptions {
  clientId: string | undefined;
  /** When false, skips background prefetch only. Click-time lookups still run when requestCloseout is invoked. */
  prefetchEnabled?: boolean;
}

type ActiveLaborEncounterLookupResult = Encounter | undefined | null;

/**
 * Guards pregnancy closeout against an in-progress Labor and Delivery encounter.
 * Prefetches when prefetchEnabled and re-fetches on requestCloseout for click-time accuracy.
 *
 * @param options.clientId - Patient id used to search for active L&D encounters.
 * @param options.prefetchEnabled - When false, skips background prefetch and clears cached encounter state.
 * @returns Modal state, prefetch cache, requestCloseout guard, and navigation handler.
 */
export function useLaborEncounterCloseoutGuard({
  clientId,
  prefetchEnabled = true,
}: UseLaborEncounterCloseoutGuardOptions) {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [laborAndDeliveryEncounter, setLaborAndDeliveryEncounter] = useState<
    Encounter | undefined
  >();
  const [isConfirmationOpened, { open: openConfirmation, close: closeConfirmation }] =
    useDisclosure(false);

  useEffect(() => {
    if (!clientId || !prefetchEnabled) {
      setLaborAndDeliveryEncounter(undefined);
      return;
    }

    let cancelled = false;
    setLaborAndDeliveryEncounter(undefined);

    (async () => {
      try {
        const encounter = await getActiveLaborAndDeliveryEncounter(medplum, clientId);
        if (!cancelled) {
          setLaborAndDeliveryEncounter(encounter);
        }
      } catch {
        if (!cancelled) {
          setLaborAndDeliveryEncounter(undefined);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId, medplum, prefetchEnabled]);

  const fetchActiveLaborEncounter =
    useCallback(async (): Promise<ActiveLaborEncounterLookupResult> => {
      if (!clientId) return undefined;

      try {
        const encounter = await getActiveLaborAndDeliveryEncounter(medplum, clientId);
        setLaborAndDeliveryEncounter(encounter);
        return encounter;
      } catch {
        toast({
          autoClose: false,
          message: LABOR_ENCOUNTER_LOOKUP_ERROR.message,
          title: LABOR_ENCOUNTER_LOOKUP_ERROR.title,
          type: "error",
        });
        return null;
      }
    }, [clientId, medplum]);

  /**
   * Checks for an active L&D encounter and opens the confirmation modal when one exists.
   *
   * @returns true when closeout may proceed (no active encounter was found).
   */
  const requestCloseout = useCallback(async (): Promise<boolean> => {
    const encounter = await fetchActiveLaborEncounter();
    if (encounter === null) return false;
    if (encounter) {
      openConfirmation();
      return false;
    }
    return true;
  }, [fetchActiveLaborEncounter, openConfirmation]);

  const confirmLaborEncounterNavigation = useCallback(
    (onBeforeNavigate?: () => void) => {
      closeConfirmation();
      if (onBeforeNavigate) onBeforeNavigate();
      if (!laborAndDeliveryEncounter?.id || !clientId) return;
      navigate(`/clients/${clientId}/encounters/${laborAndDeliveryEncounter.id}`);
    },
    [clientId, closeConfirmation, laborAndDeliveryEncounter?.id, navigate],
  );

  return {
    closeConfirmation,
    confirmLaborEncounterNavigation,
    confirmationModalProps: LABOR_ENCOUNTER_CLOSEOUT_CONFIRMATION,
    isConfirmationOpened,
    laborAndDeliveryEncounter,
    requestCloseout,
  };
}
